import { motion } from "framer-motion";
import { useState } from "react";

import SectionHeading from "@/components/SectionHeading";
import { iconRegistry } from "@/content/iconRegistry";

function PlaceholderArt({ card }) {
  const Icon = iconRegistry[card.icon];

  return (
    <div className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-card via-muted to-secondary/20 p-5">
      <div className="mb-4 flex items-center gap-2">
        <span className="h-2.5 w-2.5 rounded-full bg-highlight/80" />
        <span className="h-2.5 w-2.5 rounded-full bg-secondary/55" />
        <span className="h-2.5 w-2.5 rounded-full bg-foreground/20" />
      </div>
      <div className="space-y-3">
        <div className="h-5 w-2/3 rounded bg-secondary/45" />
        <div className="h-3 w-full rounded bg-foreground/10" />
        <div className="h-3 w-5/6 rounded bg-foreground/10" />
        <div className="h-3 w-3/5 rounded bg-foreground/10" />
      </div>
      <div className="absolute bottom-5 right-5 flex h-12 w-12 items-center justify-center rounded-2xl border border-secondary/45 bg-card/85 shadow-soft">
        <Icon className="h-6 w-6 text-highlight" />
      </div>
      <p className="absolute bottom-5 left-5 max-w-[65%] font-body text-xs font-medium leading-relaxed text-foreground/65">
        Screenshot preview coming soon
      </p>
      <div className="absolute -left-8 top-1/2 w-[130%] -translate-y-1/2 -rotate-12 bg-card/75 py-2 text-center font-sans text-xs font-semibold uppercase tracking-[0.18em] text-foreground/55">
        Preview
      </div>
    </div>
  );
}

function PreviewArt({ card }) {
  const [hasImageError, setHasImageError] = useState(false);
  const shouldShowImage = card.imageSrc && !hasImageError;
  const protection = card.previewProtection || "medium";
  const blurHeight = {
    light: "h-[24%]",
    medium: "h-[36%]",
    strong: "h-[48%]",
  }[protection];
  const imagePosition = card.imagePosition || "object-top";
  const imageScale = card.imageScale || "scale-[1.08]";

  if (!shouldShowImage) {
    return <PlaceholderArt card={card} />;
  }

  return (
    <div className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-border bg-card shadow-soft">
      <div className="flex h-8 items-center gap-2 border-b border-border bg-muted/70 px-3">
        <span className="h-2 w-2 rounded-full bg-highlight/85" />
        <span className="h-2 w-2 rounded-full bg-secondary/55" />
        <span className="h-2 w-2 rounded-full bg-foreground/20" />
        <span className="ml-auto font-sans text-[10px] font-semibold uppercase text-muted-foreground">
          Sample Page
        </span>
      </div>
      <div className="relative h-[calc(100%-2rem)] overflow-hidden bg-white">
        <img
          src={card.imageSrc}
          alt={card.imageAlt}
          className={`h-full w-full ${imageScale} object-cover ${imagePosition}`}
          onError={() => setHasImageError(true)}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background/90" />
        <div className={`absolute inset-x-0 bottom-0 ${blurHeight} bg-background/76 backdrop-blur-[3px]`} />
        <div className="absolute inset-x-[-18%] top-[48%] -rotate-12 bg-card/70 py-2 text-center font-sans text-[10px] font-semibold uppercase tracking-[0.16em] text-foreground/55">
          AI Career Advantage Preview
        </div>
      </div>
    </div>
  );
}

export default function ProductPreviewSection({ content, dataTestId, sectionId }) {
  return (
    <section className="py-16 md:py-24 bg-muted/50" data-testid={dataTestId} id={sectionId}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <SectionHeading title={content.title} description={content.description} />
        </motion.div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {content.cards.map((card, index) => (
            <motion.article
              key={card.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              className="overflow-hidden rounded-3xl border border-border bg-card shadow-soft"
            >
              <PreviewArt card={card} />
              <div className="p-5">
                <h3 className="font-sans text-lg font-semibold text-foreground">{card.title}</h3>
                <p className="mt-2 font-body text-sm leading-relaxed text-muted-foreground">{card.description}</p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
